import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { useModal } from './ModalContext';
const TaskContext = createContext();

// Custom hook to use the context
export const useTask = () => {
    const context = useContext(TaskContext);
    
    if (context === undefined) {
      throw new Error('useTask must be used within a TaskProvider');
    }
    return context;
  };

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [calenderTasks, setCalenderTasks] = useState([]);
  const {openErrorModal} = useModal();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskPriorities, setTaskPriorities] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [taskSources, setTaskSources] = useState([]);
  const [taskRecurrenceOptions, setTaskRecurrenceOptions] = useState([]);

  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    itemType: 'all',
    startDate: null,
    endDate: null,
    assignedTo: null,
    searchQuery: '',
  });

  const [tasksPagination, setTasksPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    totalCount: 0,
    pageSize: 10,
  });

  const fetchTaskUtility = async () => {
    try {
      const response = await api.get('/calendar/tasks/utility/');
      if(response.status === 200) {
        setTaskTypes(response.data.task_types);
        setTaskPriorities(response.data.task_priorities);
        setTaskStatuses(response.data.task_statuses);
        setTaskSources(response.data.task_sources);
        setTaskRecurrenceOptions(response.data.task_recurrence_patterns);
      }
    } catch (err) {
      console.error('Error fetching task utility:', err);
    }
  };
  // Fetch tasks with optional filtering (paginated when leadId is set)
  const fetchTasks = useCallback(async (leadId = null, params = {}) => {
    if (!leadId) {
      setLoading(false);
      return;
    }

    const page = params.page != null ? Number(params.page) : 1;
    const isAppend = page > 1;

    if (!isAppend) {
      setLoading(true);
    }
    setError(null);

    try {
      const url = `/calendar/leads/${leadId}/tasks/`;
      const query = {
        page: page < 1 ? 1 : page,
        page_size: params.page_size != null ? Number(params.page_size) : 10,
      };

      if (filters.status && filters.status !== 'all') query.status = filters.status;
      if (filters.priority && filters.priority !== 'all') query.priority = filters.priority;
      if (filters.itemType && filters.itemType !== 'all') query.item_type = filters.itemType;
      if (filters.startDate) query.start_after = filters.startDate;
      if (filters.endDate) query.start_before = filters.endDate;
      if (filters.assignedTo) query.assigned_to = filters.assignedTo;
      if (filters.searchQuery) query.search = filters.searchQuery;

      const response = await api.get(url, { params: query });
      const data = response.data;

      if (data && Array.isArray(data.results)) {
        if (isAppend) {
          setTasks((prev) => [...prev, ...data.results]);
        } else {
          setTasks(data.results);
        }
        setTasksPagination({
          currentPage: data.current_page || 1,
          totalPages: data.total_pages || 1,
          hasNext: data.has_next || false,
          totalCount: data.count ?? data.results.length,
          pageSize: data.page_size || query.page_size,
        });
      } else {
        const list = Array.isArray(data) ? data : [];
        setTasks(list);
        setTasksPagination({
          currentPage: 1,
          totalPages: 1,
          hasNext: false,
          totalCount: list.length,
          pageSize: list.length || 10,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Create a new task
  const createTask = async (leadId, taskData, asset_id = null) => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (leadId) { 
        if(asset_id) {
          response = await api.post(`/calendar/leads/${leadId}/tasks/${asset_id}/`, taskData);
        } else {
          response = await api.post(`/calendar/leads/${leadId}/tasks/`, taskData);
        }
        setTasks((prevTasks) => [response.data, ...prevTasks]);
        setTasksPagination((p) => ({
          ...p,
          totalCount: (p.totalCount ?? 0) + 1,
        }));
      } else {
        response = await api.post(`/calendar/tasks/`, taskData);
        setCalenderTasks(prevTasks => [response.data, ...prevTasks]);
      }
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create task');
      console.error('Error creating task:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).replace(/_/g, ' ');
  };
  

  // Update an existing task
  const updateTask = async (taskId, taskData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.patch(`/calendar/tasks/${taskId}/`, taskData);
      if(response.status === 200){
        setTasks(prevTasks => 
          prevTasks.map(task => task.id === taskId ? response.data : task)
        );
        if (selectedTask?.id === taskId) {
          setSelectedTask(response.data);
        }
        return response.data;
      } else {
        openErrorModal("Error updating task", response.data.description);
      }
    } catch (err) {
      const errorData = err.response?.data;
    
      let formattedMessage = 'Something went wrong.';
    
      if (errorData && typeof errorData === 'object') {
        formattedMessage = Object.entries(errorData)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${capitalizeFirstLetter(key)}: ${value.join(', ')}`;
            }
            return `${capitalizeFirstLetter(key)}: ${value}`;
          })
          .join('\n');
      }
    
      openErrorModal("Error updating task", formattedMessage);
      console.error('Error updating task:', err);
      throw err;
    }
     finally {
      setLoading(false);
    }
  };

  // Mark task as completed
  const completeTask = async (taskId, completedAt = new Date().toISOString()) => {
    return updateTask(taskId, { 
      status: 'completed',
      completed_at: completedAt
    });
  };

  // Mark task as cancelled
  const cancelTask = async (taskId, reason = '') => {
    return updateTask(taskId, {
      status: 'cancelled',
      metadata: { cancellation_reason: reason }
    });
  };

  // Get a single task by ID
  const fetchTaskById = async (taskId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/calendar/tasks/${taskId}/`);
      setSelectedTask(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch task details');
      console.error('Error fetching task details:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add a note to a task
  const addTaskNote = async (taskId, noteData) => {
    try {
      const response = await api.post(`/calendar/tasks/${taskId}/notes/`, noteData);
      
      // Update the selected task if it's the one we're adding a note to
      if (selectedTask?.id === taskId) {
        setSelectedTask(prev => ({
          ...prev,
          notes: [...(prev.notes || []), response.data]
        }));
      }
      
      return response.data;
    } catch (err) {
      console.error('Error adding task note:', err);
      throw err;
    }
  };

  const deleteTask = async (task_id) => {
    try{
      const response = await api.delete(`/calendar/tasks/${task_id}/delete/`);
      if (response.status === 200) {
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== task_id));
        setTasksPagination((p) => ({
          ...p,
          totalCount: Math.max(0, (p.totalCount ?? 0) - 1),
        }));
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }

  const fetchCalenderTasks = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching calendar tasks with filters:', filters);
      const startTime = Date.now();
      
      // Build query parameters
      const params = {};
      if (filters.agent_id) {
        params.agent_id = filters.agent_id;
      } else if (filters.team_id) {
        params.team_id = filters.team_id;
      }
      
      const response = await api.get('/calendar/tasks/', {
        params,
        timeout: 30000 // 30 second timeout
      });
      
      const endTime = Date.now();
      console.log(`Calendar tasks fetched in ${endTime - startTime}ms`);
      
      let tasks = response.data.results || response.data || [];
      
      // Ensure tasks is always an array
      if (!Array.isArray(tasks)) {
        console.warn('Tasks data is not an array, converting to empty array');
        tasks = [];
      }
      
      console.log(`Loaded ${tasks.length} calendar tasks`);
      setCalenderTasks(tasks);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch calendar tasks';
      setError(errorMsg);
      console.error('Error fetching calendar tasks:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      // Set empty array on error to prevent continuous retries
      setCalenderTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalenderTaskCounts = useCallback(async (filters = {}) => {
    // Don't set global loading state for counts - they're fast and separate
    setError(null);
    
    try {
      // Build query parameters
      const params = {};
      if (filters.agent_id) {
        params.agent_id = filters.agent_id;
      } else if (filters.team_id) {
        params.team_id = filters.team_id;
      }
      
      const response = await api.get('/calendar/tasks/counts/', { params });
      let counts = response.data || [];
      
      // Ensure counts is always an array
      if (!Array.isArray(counts)) {
        console.warn('Counts data is not an array, converting to empty array');
        counts = [];
      }
      
      return counts;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch calendar task counts');
      console.error('Error fetching calendar task counts:', err);
      return [];
    }
  }, []);

  const fetchTasksByDateAndType = useCallback(async (date, itemType, filters = {}) => {
    // Don't set global loading state - use local loading in component
    setError(null);
    
    try {
      // Build query parameters
      const params = {
        date: date,
        item_type: itemType
      };
      
      // Add filter parameters (same logic as fetchCalenderTaskCounts)
      if (filters.agent_id) {
        params.agent_id = filters.agent_id;
      } else if (filters.team_id) {
        params.team_id = filters.team_id;
      }
      
      const response = await api.get('/calendar/tasks/by-date-type/', { params });
      let tasks = response.data || [];
      
      // Ensure tasks is always an array
      if (!Array.isArray(tasks)) {
        console.warn('Tasks data is not an array, converting to empty array');
        tasks = [];
      }
      
      return tasks;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tasks by date and type');
      console.error('Error fetching tasks by date and type:', err);
      return [];
    }
  }, []);

  // Clear all tasks and reset state
  const clearTasks = () => {
    setTasks([]);
    setCalenderTasks([]);
    setSelectedTask(null);
    setTasksPagination({
      currentPage: 1,
      totalPages: 1,
      hasNext: false,
      totalCount: 0,
      pageSize: 10,
    });
  };

  // Update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Reset filters to default
  const resetFilters = () => {
    setFilters({
      status: 'all',
      priority: 'all',
      itemType: 'all',
      startDate: null,
      endDate: null,
      assignedTo: null,
      searchQuery: '',
    });
  };

  const getStatusColors = (status) => {
    const priorityColorMap = {
      high: '#F87171',   // red-400
      medium: '#FBBF24', // yellow-400
      low: '#34D399',    // green-400
      pending: '#FB923C', // orange-400
      in_progress: '#38BDF8', // blue-400
      completed: '#4ADE80', // green-400
      cancelled: '#FB7185', // red-400
      on_hold: '#E879F9', // purple-400
    };
  
    const bgColorMap = {
      high: '#FEF2F2',    // red-50
      medium: '#FFFBEB',  // yellow-50
      low: '#ECFDF5',     // green-50
      pending: '#FFF7ED', // Light orange
      in_progress: '#F0F9FF', // Light blue
      completed: '#F0FDF4', // Light green
      cancelled: '#FEF2F2', // Light red
      on_hold: '#FDF4FF', // Light purple
    };
  

    switch (status?.toLowerCase()) {
      case 'pending':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'in_progress':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'completed':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'cancelled':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'on_hold':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      default:
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
    }
  };

  // Provide all values and functions
  const value = {
    tasks,
    tasksPagination,
    loading,
    error,
    selectedTask,
    filters,
    fetchTasks,
    createTask,
    updateTask,
    completeTask,
    cancelTask,
    fetchTaskById,
    setSelectedTask,
    addTaskNote,
    clearTasks,
    updateFilters,
    resetFilters,
    fetchTaskUtility,
    taskTypes,
    taskPriorities,
    taskStatuses,
    taskSources,
    taskRecurrenceOptions,
    deleteTask,
    fetchCalenderTasks,
    fetchCalenderTaskCounts,
    fetchTasksByDateAndType,
    calenderTasks,
    getStatusColors
  };

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
};
